/**
 * The leave contract — types, requests, and creates the approval a caller can see through.
 *
 * Copies employee.contract.ts's conventions exactly (see that file's header for the full
 * rationale): DTOs distinct from the Prisma models, every DTO carrying what `ResourceTarget` needs
 * to authorise it, and `paginatedSchema()` for every list.
 *
 * WHOLE DAY, HALF DAY, OR HOURLY — A PER-REQUEST CHOICE, ON EVERY LEAVE TYPE. There is no
 * type-level unit: any type's balance stays in days, and an hourly request (`hours`,
 * `leaveHoursSchema`, always a single date) is converted to its day-equivalent server-side —
 * `hours / the requester's shift's fullDayThresholdHours` (see `@hrms/attendance`'s
 * `resolveEffectiveShift`/`DEFAULT_SHIFT`) — and debited from that SAME balance a whole/half-day
 * request would debit. See `evaluateLeaveRequest` in leave.ts for the conversion. Partial-day
 * attendance marking for the hours actually taken is still a deliberate follow-up (today an hourly
 * request simply never touches attendance — see the same function's comment).
 *
 * APPROVAL GOES THROUGH `@hrms/workflow`, NEVER A HAND-ROLLED STATUS FLIP (CLAUDE.md invariant 6).
 * `leaveRequestSchema.status` mirrors whatever the underlying `WorkflowInstance` says; the route
 * never writes it directly except to copy that value across after calling the engine.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  accrualFrequencySchema,
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  leaveDaysSchema,
  leaveHoursSchema,
  leaveRequestStatusSchema,
  leaveSessionSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { leaveCancellationInputSchema, leaveCancellationRequestSchema } from '../leave-changes.js';
import { leaveTypeRuleExtensionSchema, refineLeaveRuleExtension } from '../leave-policy-rules.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const leaveYearBasisSchema = z.enum(['fiscal', 'calendar']);

export const leaveTypeSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  /** Null means the type applies to every legal entity in the org. */
  legalEntityId: uuidSchema.nullable(),
  code: z.string(),
  name: z.string(),
  isPaid: z.boolean(),
  accrualFrequency: accrualFrequencySchema,
  isActive: z.boolean(),
  annualQuota: leaveDaysSchema.or(z.literal('0')).nullable(),
  carryForwardCap: leaveDaysSchema.or(z.literal('0')).nullable(),
  sandwichPolicy: z.boolean(),
  allowHalfDay: z.boolean(),
  minNoticeDays: z.number().int(),
  maxBackdateDays: z.number().int().nullable(),
  maxConsecutiveDays: z.number().int().nullable(),
  isCompOff: z.boolean(),
  creditValidityDays: z.number().int().nullable(),
  restrictedHolidayQuota: z.boolean(),
  /**
   * Days after which an attachment becomes mandatory. Replaces the old boolean
   * `requiresAttachment` entirely: `null` means never required, `0` means always required.
   */
  attachmentRequiredAfterDays: z.number().int().nullable(),
  leaveYearBasis: leaveYearBasisSchema,
  /**
   * Null means no tenure restriction. When set, a request is blocked unless the target person's
   * tenure (days between `Employment.joinedOn` and the request's `startDate`) is at least this
   * many days — e.g. "no Earned Leave in the first 90 days".
   */
  minTenureDays: z.number().int().nullable(),
  /**
   * Empty array means everyone eligible. A non-empty array (e.g. `['female']`) restricts this type
   * to people whose `Person.gender` is in the list.
   */
  eligibleGenders: z.array(z.string()),
  /**
   * Empty array means everyone eligible, same convention as `eligibleGenders`. Opt-in, unset by
   * default — see the schema's own comment on why this isn't wired to maternity/paternity types
   * automatically.
   */
  eligibleMaritalStatuses: z.array(z.string()),
  /** False means a person whose `Employment.status` is `notice_period` cannot request this type. */
  allowedDuringNoticePeriod: z.boolean(),
  /** False means a person whose `Employment.status` is `probation` cannot request this type. */
  allowedDuringProbation: z.boolean(),
}).merge(leaveTypeRuleExtensionSchema);
export type LeaveType = z.infer<typeof leaveTypeSchema>;

/**
 * One currently-valid balance row — the head of that person/leave-type/leave-year's history, never
 * the whole history. `validFrom` is included so a client can show "as of" without a second call.
 */
export const leaveBalanceSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  leaveYear: z.number().int(),
  balanceDays: leaveDaysSchema.or(z.literal('0')),
  quotaDays: leaveDaysSchema.or(z.literal('0')),
  accruedDays: leaveDaysSchema.or(z.literal('0')),
  /** Paid days of APPROVED requests in this leave year — derived from request status, not the
   * ledger (which debits at filing and so also holds pending days). See apps/api/src/lib/leave-usage.ts. */
  usedDays: leaveDaysSchema.or(z.literal('0')),
  /** Paid days of PENDING requests in this leave year. Already debited from `balanceDays` at filing,
   * so `balanceDays` is net of this — never subtract it again. */
  pendingDays: leaveDaysSchema.or(z.literal('0')),
  percentRemaining: z.number(),
  /** When unused days on this balance lapse (carry-forward cap or leave-year end). */
  lapsesOn: dateOnlySchema.nullable(),
  validFrom: dateOnlySchema,
});
export type LeaveBalance = z.infer<typeof leaveBalanceSchema>;

/**
 * Carries what `ResourceTarget` needs via `personId` — see convention 2 in employee.contract.ts.
 * `personId` here is ALWAYS a Person id (it is how the Prisma column is defined); the route must
 * still resolve a manager chain from it the same way employee.ts's `resolveTarget` does, because a
 * `LeaveRequest` row alone does not carry the requester's current manager.
 */
export const leaveRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string(),
  /** Who actually submitted the request — may differ from `personId` when filed on their behalf. */
  filedByPersonId: uuidSchema,
  leaveTypeId: uuidSchema,
  leaveTypeName: z.string(),
  /** A short human-readable id, e.g. "SL-6729" — for display, never a lookup key. */
  requestCode: z.string(),
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  startSession: leaveSessionSchema,
  endSession: leaveSessionSchema,
  /**
   * Set only for an hourly request (always `startDate === endDate` when this is set) — the raw
   * hour count the caller asked for, kept alongside `days` (its day-equivalent, what actually gets
   * debited) purely so a client can show "3 hours" instead of a day-fraction like "0.38". Null for
   * a whole/half-day request. See this file's header for the hours -> days conversion.
   */
  hours: leaveHoursSchema.nullable(),
  /** Server-computed; clients never send this — the date range/sessions/holiday calendar, OR (for
   * an hourly request) `hours` converted to its day-equivalent. Always what's actually debited. */
  days: leaveDaysSchema,
  paidDays: leaveDaysSchema.or(z.literal('0')),
  unpaidDays: leaveDaysSchema.or(z.literal('0')),
  reason: z.string().nullable(),
  attachmentUrl: z.string().url().nullable(),
  /**
   * Who covers this person's work while they're out — a REAL person, resolved server-side, never
   * free text parsed out of `reason`. Both null together when no delegate was named.
   */
  taskDelegatePersonId: uuidSchema.nullable(),
  taskDelegateName: z.string().nullable(),
  /** Set only when this is a restricted-holiday type request. */
  holidayId: uuidSchema.nullable(),
  status: leaveRequestStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  currentApproverName: z.string().nullable(),
  currentStepNo: z.number().int().nullable(),
  totalSteps: z.number().int().nullable(),
  createdAt: instantSchema,
});
export type LeaveRequest = z.infer<typeof leaveRequestSchema>;

/**
 * One `WorkflowStep` currently or previously assigned to the caller, joined back onto its
 * `LeaveRequest` — everything `leaveRequestSchema` already carries, plus the step-level fields an
 * approvals inbox needs: when it's due, whether it's been escalated, and (once decided) what the
 * caller decided and when. `decision`/`decidedAt`/`decidedComment` are all null for a pending step.
 */
/**
 * One entry on a team/org leave calendar — deliberately a much smaller shape than `leaveRequestSchema`.
 * NEVER `reason` or `attachmentUrl`: a calendar is shown to people who are not the requester's
 * approver (peers, in the plain-employee case), so those free-text/attachment fields have no business
 * appearing here at all — there is no field for either, by design, not a server-side redaction of one.
 * `leaveTypeName` is `null` for a plain-employee caller and the real name for a manager-or-above
 * caller — see `getCalendar`'s handler for exactly which grant makes the difference.
 */
export const leaveCalendarEntrySchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  /** Never 'rejected'/'cancelled' — nobody needs a calendar showing leave that isn't happening. */
  status: z.enum(['pending', 'approved']),
  leaveTypeName: z.string().nullable(),
});
export type LeaveCalendarEntry = z.infer<typeof leaveCalendarEntrySchema>;

export const leaveApprovalSchema = leaveRequestSchema.extend({
  stepId: uuidSchema,
  dueAt: instantSchema.nullable(),
  escalatedAt: instantSchema.nullable(),
  decision: z.enum(['approved', 'rejected', 'delegated', 'skipped']).nullable(),
  /** Maps to `WorkflowStep.resolvedAt`. */
  decidedAt: instantSchema.nullable(),
  /** Maps to `WorkflowStep.comment`. */
  decidedComment: z.string().nullable(),
});
export type LeaveApproval = z.infer<typeof leaveApprovalSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listLeaveTypesQuerySchema = paginationQuerySchema.extend({
  /**
   * Only meaningful for a caller holding `leave.policy:manage` — an HR admin managing the catalog,
   * who needs to see a deactivated type to reactivate or otherwise edit it. Anyone else, or this
   * left false/absent, always sees `isActive: true` rows only; the route enforces this server-side
   * regardless of what a caller sends — same pattern as `listHolidaysQuerySchema.includeUnpublished`.
   */
  includeInactive: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
});

/**
 * Every writable field from `leaveTypeSchema` except `id`/`orgId`. Defaults mirror
 * `provisionLeaveDefaults`'s own defaults where one exists (`accrualFrequency: 'none'`,
 * `leaveYearBasis: 'fiscal'`, `allowHalfDay: true`, ...).
 */
export const createLeaveTypeBodySchema = z
  .object({
    legalEntityId: uuidSchema.nullable().default(null),
    code: z.string().min(1),
    name: z.string().min(1),
    isPaid: z.boolean().default(true),
    accrualFrequency: accrualFrequencySchema.default('none'),
    isActive: z.boolean().default(true),
    annualQuota: leaveDaysSchema.or(z.literal('0')).nullable().default(null),
    carryForwardCap: leaveDaysSchema.or(z.literal('0')).nullable().default(null),
    sandwichPolicy: z.boolean().default(false),
    allowHalfDay: z.boolean().default(true),
    minNoticeDays: z.number().int().default(0),
    maxBackdateDays: z.number().int().nullable().default(null),
    maxConsecutiveDays: z.number().int().nullable().default(null),
    isCompOff: z.boolean().default(false),
    creditValidityDays: z.number().int().nullable().default(null),
    restrictedHolidayQuota: z.boolean().default(false),
    attachmentRequiredAfterDays: z.number().int().nullable().default(null),
    leaveYearBasis: leaveYearBasisSchema.default('fiscal'),
    minTenureDays: z.number().int().nullable().default(null),
    eligibleGenders: z.array(z.string()).default([]),
    eligibleMaritalStatuses: z.array(z.string()).default([]),
    allowedDuringNoticePeriod: z.boolean().default(true),
    allowedDuringProbation: z.boolean().default(true),
  })
  // Phase 4c rule fields (leave-policy-rules.ts): all optional, none defaulted — absent = not set.
  .merge(leaveTypeRuleExtensionSchema)
  .strict()
  .superRefine(refineLeaveRuleExtension);

/**
 * A normal partial PATCH — every field optional, `code` included. `code` is still accepted here;
 * the route is what refuses to apply a `code` change once any `LeaveRequest` or `LeaveBalance`
 * references the type (an unreferenced type's code CAN still change) — see leave.ts's `updateLeaveType`.
 * Deliberately no delete endpoint: types are referenced by history, so deactivate via
 * `isActive: false` instead.
 */
export const updateLeaveTypeBodySchema = z
  .object({
    legalEntityId: uuidSchema.nullable().optional(),
    code: z.string().min(1).optional(),
    name: z.string().min(1).optional(),
    isPaid: z.boolean().optional(),
    accrualFrequency: accrualFrequencySchema.optional(),
    isActive: z.boolean().optional(),
    annualQuota: leaveDaysSchema.or(z.literal('0')).nullable().optional(),
    carryForwardCap: leaveDaysSchema.or(z.literal('0')).nullable().optional(),
    sandwichPolicy: z.boolean().optional(),
    allowHalfDay: z.boolean().optional(),
    minNoticeDays: z.number().int().optional(),
    maxBackdateDays: z.number().int().nullable().optional(),
    maxConsecutiveDays: z.number().int().nullable().optional(),
    isCompOff: z.boolean().optional(),
    creditValidityDays: z.number().int().nullable().optional(),
    restrictedHolidayQuota: z.boolean().optional(),
    attachmentRequiredAfterDays: z.number().int().nullable().optional(),
    leaveYearBasis: leaveYearBasisSchema.optional(),
    minTenureDays: z.number().int().nullable().optional(),
    eligibleGenders: z.array(z.string()).optional(),
    eligibleMaritalStatuses: z.array(z.string()).optional(),
    allowedDuringNoticePeriod: z.boolean().optional(),
    allowedDuringProbation: z.boolean().optional(),
  })
  .merge(leaveTypeRuleExtensionSchema)
  .strict()
  .superRefine(refineLeaveRuleExtension);

export const leaveBalanceQuerySchema = paginationQuerySchema.extend({
  /** Whose balance to read. Defaults to the caller; reading anyone else needs leave.balance:read
   * over that person, per `can()` — see the route. */
  personId: uuidSchema.optional(),
  leaveYear: z.coerce.number().int().optional(),
});

/**
 * HR manually correcting a balance outside the normal debit/credit/accrual flow — e.g. a data-entry
 * fix, a policy exception, a one-off goodwill grant. `delta` is unsigned (`leaveDaysSchema`, the
 * same decimal-string shape `leaveBalanceSchema.balanceDays` itself uses — never a JS number, per
 * CLAUDE.md invariant 2); `direction` carries the sign explicitly so the caller states intent
 * ("add" or "subtract") rather than the API inferring it from a `+`/`-` prefix. `reason` is
 * mandatory free text: unlike a debit/credit that explains itself via `leaveRequestId`, an
 * adjustment has no other record of WHY, so the caller must say.
 */
export const manualLeaveBalanceAdjustmentBodySchema = z
  .object({
    personId: uuidSchema,
    leaveTypeId: uuidSchema,
    leaveYear: z.number().int(),
    delta: leaveDaysSchema,
    direction: z.enum(['credit', 'debit']),
    reason: z.string().min(1).max(500),
  })
  .strict();

export const listLeaveRequestsQuerySchema = paginationQuerySchema.extend({
  /** Defaults to the caller's own requests. */
  personId: uuidSchema.optional(),
  leaveTypeId: uuidSchema.optional(),
  status: leaveRequestStatusSchema.optional(),
});

export const createLeaveRequestBodySchema = z
  .object({
    leaveTypeId: uuidSchema,
    startDate: dateOnlySchema,
    endDate: dateOnlySchema,
    /** Which half of the start/end date is covered. The server derives `days` from these. Ignored
     * whenever `hours` below is set — an hourly request states its own amount instead. */
    startSession: leaveSessionSchema.default('full'),
    endSession: leaveSessionSchema.default('full'),
    /**
     * Present means "this is an hourly request, not a whole/half-day one" — a per-request choice on
     * ANY leave type (see this file's header), never gated by the type itself. When set,
     * `startDate`/`endDate` must be equal: an hourly request is always a single date.
     */
    hours: leaveHoursSchema.optional(),
    /** On-behalf-of filing. Absent means the caller is filing for themself. */
    personId: uuidSchema.optional(),
    reason: z.string().max(500).optional(),
    attachmentUrl: z.string().url().optional(),
    /**
     * Who covers this person's work while they're out. A real Person id in the same org, resolved
     * and validated server-side — never delegate-to-self, and never free text. Absent means no
     * delegate is named.
     */
    taskDelegatePersonId: uuidSchema.optional(),
    /** Set only when filing against a restricted holiday. */
    holidayId: uuidSchema.optional(),
  })
  // `.strict()` because `days` used to be a client-supplied field: silently stripping it (the zod
  // default) would let an un-migrated client believe its day count was honoured.
  .strict()
  .refine((body) => body.endDate >= body.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  });

/**
 * `from`/`to` are both required (unlike every other date-range-ish query here, which defaults to
 * "current period") — a calendar with no window would mean "the whole history of the org", which is
 * never what a caller wants. Capped at 93 days (a wide quarter) for the same reason `listApprovals`
 * caps its page size: a calendar is a UI surface, not a reporting export.
 */
export const getLeaveCalendarQuerySchema = paginationQuerySchema
  .extend({
    from: dateOnlySchema,
    to: dateOnlySchema,
    /** HR/org-scope only — narrows a large org's calendar. Ignored for a plain-employee or
     * manager-tier caller, whose visible team is already resolved from their own position in the
     * org chart, not from a filter they supply. */
    departmentId: uuidSchema.optional(),
    locationId: uuidSchema.optional(),
  })
  .refine((query) => query.to >= query.from, {
    message: 'to must be on or after from',
    path: ['to'],
  })
  .refine(
    (query) => {
      const days = Math.round(
        (Date.parse(`${query.to}T00:00:00Z`) - Date.parse(`${query.from}T00:00:00Z`)) / 86_400_000,
      );
      return days <= 93;
    },
    { message: 'The from/to window cannot exceed 93 days', path: ['to'] },
  );

export const listLeaveApprovalsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(['pending', 'decided']).default('pending'),
});

export const decideLeaveRequestBodySchema = z.object({
  comment: z.string().max(500).optional(),
});

/**
 * `{}` / `{ comment }` still means "cancel the whole request" exactly as before; `kind: 'partial'`
 * with `fromDate`/`toDate` gives back part of it (ADR 0031 addendum, Phase 4b). The schema lives in
 * `../leave-changes.js` with the rest of the cancellation vocabulary.
 */
export const cancelLeaveRequestBodySchema = leaveCancellationInputSchema;

// ---------------------------------------------------------------------------
// Holidays
// ---------------------------------------------------------------------------

/** A location's holiday calendar row. `isOptional` marks a restricted/floating holiday. */
export const holidaySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  locationId: uuidSchema,
  date: dateOnlySchema,
  name: z.string(),
  isOptional: z.boolean(),
  /** Draft vs Published — the Holiday Management console's staged bulk-publish workflow. A caller
   * without `holiday_calendar:manage` never sees a draft row at all, regardless of query params. */
  isPublished: z.boolean(),
});
export type Holiday = z.infer<typeof holidaySchema>;

export const listHolidaysQuerySchema = paginationQuerySchema.extend({
  locationId: uuidSchema.optional(),
  year: z.coerce.number().int().optional(),
  /**
   * Only meaningful for a caller holding `holiday_calendar:manage` — an HR admin staging holidays
   * before publish. Anyone else, or this left false/absent, always sees isPublished: true rows only;
   * the route enforces this server-side regardless of what a caller sends.
   */
  includeUnpublished: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
});

/**
 * One holiday EVENT applied to N locations at once, per the Holiday Management console's design —
 * the model is one row per (orgId, locationId, date), so this creates N rows atomically, all sharing
 * the same name/date/isOptional/isPublished.
 */
export const createHolidayBodySchema = z.object({
  name: z.string().min(1),
  date: dateOnlySchema,
  isOptional: z.boolean().default(false),
  isPublished: z.boolean().default(true),
  locationIds: z.array(uuidSchema).min(1),
});

/** A normal partial PATCH, editing ONE row (one location) at a time. */
export const updateHolidayBodySchema = z.object({
  name: z.string().min(1).optional(),
  date: dateOnlySchema.optional(),
  isOptional: z.boolean().optional(),
  isPublished: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Quote
// ---------------------------------------------------------------------------

export const quoteLeaveRequestQuerySchema = z.object({
  leaveTypeId: uuidSchema,
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  startSession: leaveSessionSchema,
  endSession: leaveSessionSchema,
  /** Same rule as `createLeaveRequestBodySchema.hours` — present means an hourly quote. */
  hours: leaveHoursSchema.optional(),
  /** Whose balance to quote against. Defaults to the caller. */
  personId: uuidSchema.optional(),
});

/**
 * A dry-run of what a request would cost, before the caller commits to filing it — chargeable vs.
 * paid/unpaid days, the resulting balance, and the holidays inside the range, so the client never
 * has to reimplement calendar or balance math to preview a request.
 */
export const leaveQuoteSchema = z.object({
  chargeableDays: leaveDaysSchema.or(z.literal('0')),
  paidDays: leaveDaysSchema.or(z.literal('0')),
  unpaidDays: leaveDaysSchema.or(z.literal('0')),
  balanceAfter: leaveDaysSchema.or(z.literal('0')),
  holidaysInRange: z.array(z.object({ date: dateOnlySchema, name: z.string() })),
  warnings: z.array(z.string()),
});
export type LeaveQuote = z.infer<typeof leaveQuoteSchema>;

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

export const leaveSummaryQuerySchema = z.object({
  leaveYear: z.coerce.number().int().optional(),
});

const leaveSummaryPerTypeSchema = z.object({
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  /** Remaining now — net of pending, same figure as the type's `LeaveBalance.balanceDays`. */
  balanceDays: leaveDaysSchema.or(z.literal('0')),
  quotaDays: leaveDaysSchema.or(z.literal('0')),
  /** Same definitions as `LeaveBalance.usedDays` / `pendingDays`, and — unlike a balance — present
   * for comp-off too, which is credit-funded and has no `LeaveBalance` row. */
  usedDays: leaveDaysSchema.or(z.literal('0')),
  pendingDays: leaveDaysSchema.or(z.literal('0')),
  isCompOff: z.boolean(),
  /** The leave year this row's figures are for, resolved from the type's own basis. */
  leaveYear: z.number().int(),
});

/**
 * A dashboard-shaped roll-up across every leave type, for the caller's CURRENT leave year (each
 * type resolved by its own basis: calendar types reset in January, the rest follow the legal
 * entity's fiscal year).
 *
 * `availableTotalDays` is the sum of `perType[].balanceDays` (comp-off's unexpired credit included) and is
 * ALREADY net of pending days (a request debits the balance when filed). `takenDays` is approved
 * paid days; `pendingApprovalDays` is pending paid days — the two never overlap.
 */
export const leaveSummarySchema = z.object({
  availableTotalDays: leaveDaysSchema.or(z.literal('0')),
  takenDays: leaveDaysSchema.or(z.literal('0')),
  pendingApprovalDays: leaveDaysSchema.or(z.literal('0')),
  /** How many requests are pending — the same set the My Requests tab badge counts. */
  pendingRequestCount: z.number().int(),
  /** The legal entity's current fiscal year label (its start year) and the month it starts in. */
  leaveYear: z.number().int(),
  fiscalYearStartMonth: z.number().int().min(1).max(12),
  perType: z.array(leaveSummaryPerTypeSchema),
});
export type LeaveSummary = z.infer<typeof leaveSummarySchema>;

// ---------------------------------------------------------------------------
// Comp-off credits
// ---------------------------------------------------------------------------

export const compOffCreditStatusSchema = z.enum(['pending', 'approved', 'consumed', 'expired']);

export const compOffCreditSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  workedDate: dateOnlySchema,
  days: leaveDaysSchema.or(z.literal('0')),
  expiresOn: dateOnlySchema,
  status: compOffCreditStatusSchema,
});
export type CompOffCredit = z.infer<typeof compOffCreditSchema>;

export const listCompOffCreditsQuerySchema = paginationQuerySchema.extend({
  personId: uuidSchema.optional(),
  status: compOffCreditStatusSchema.optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const leaveContract = c.router(
  {
    listTypes: {
      method: 'GET',
      path: '/types',
      query: listLeaveTypesQuerySchema,
      responses: {
        200: paginatedSchema(leaveTypeSchema),
        401: errorBodySchema,
      },
      summary: 'List active leave types for the org',
      metadata: routeMeta('leave.policy:read'),
    },

    createLeaveType: {
      method: 'POST',
      path: '/types',
      body: createLeaveTypeBodySchema,
      responses: {
        201: leaveTypeSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a leave type (leave.policy:manage)',
      metadata: routeMeta('leave.policy:manage'),
    },

    updateLeaveType: {
      method: 'PATCH',
      path: '/types/:leaveTypeId',
      body: updateLeaveTypeBodySchema,
      responses: {
        200: leaveTypeSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        // `code` is frozen once a LeaveRequest or LeaveBalance references the type — same 409 shape
        // as createHoliday/updateHoliday's unique-constraint collisions.
        409: errorBodySchema,
      },
      summary:
        'Edit a leave type — code is immutable once referenced by history (leave.policy:manage)',
      metadata: routeMeta('leave.policy:manage'),
    },

    listBalances: {
      method: 'GET',
      path: '/balances',
      query: leaveBalanceQuerySchema,
      responses: {
        200: paginatedSchema(leaveBalanceSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Current leave balances, one row per leave type, for the caller or a visible report',
      metadata: routeMeta('leave.balance:read'),
    },

    adjustBalance: {
      method: 'POST',
      path: '/balances/adjust',
      body: manualLeaveBalanceAdjustmentBodySchema,
      responses: {
        200: leaveBalanceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        // Unknown/inactive leave type for this org — same 422 shape evaluateLeaveRequest's (b) step
        // uses for the identical case on createRequest.
        422: errorBodySchema,
        // The row was closed by a concurrent writer between lock and close — same shape
        // closeAndOpenBalanceRow itself throws, and the same 409 createLeaveType/updateLeaveType use
        // for their own concurrent-write cases.
        409: errorBodySchema,
      },
      summary: 'HR manually adjusts a person’s leave balance, with a required reason (leave.balance:adjust)',
      metadata: routeMeta('leave.balance:adjust'),
    },

    quoteRequest: {
      method: 'GET',
      path: '/quote',
      query: quoteLeaveRequestQuerySchema,
      responses: {
        200: leaveQuoteSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Preview chargeable/paid/unpaid days and resulting balance before filing a request',
      metadata: routeMeta('leave:request'),
    },

    listRequests: {
      method: 'GET',
      path: '/requests',
      query: listLeaveRequestsQuerySchema,
      responses: {
        200: paginatedSchema(leaveRequestSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List leave requests in the caller's visible scope",
      metadata: routeMeta('leave:read'),
    },

    getRequest: {
      method: 'GET',
      path: '/requests/:leaveRequestId',
      responses: {
        200: leaveRequestSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one leave request',
      metadata: routeMeta('leave:read'),
    },

    listApprovals: {
      method: 'GET',
      path: '/approvals',
      query: listLeaveApprovalsQuerySchema,
      responses: {
        200: paginatedSchema(leaveApprovalSchema),
        401: errorBodySchema,
      },
      summary:
        'Leave requests currently assigned to the caller for decision, or already decided by them',
      metadata: routeMeta(null),
    },

    createRequest: {
      method: 'POST',
      path: '/requests',
      body: createLeaveRequestBodySchema,
      responses: {
        201: leaveRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'File a leave request for the caller and start its approval workflow',
      metadata: routeMeta('leave:request'),
    },

    approveRequest: {
      method: 'POST',
      path: '/requests/:leaveRequestId/approve',
      body: decideLeaveRequestBodySchema,
      responses: {
        200: leaveRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve the step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    rejectRequest: {
      method: 'POST',
      path: '/requests/:leaveRequestId/reject',
      body: decideLeaveRequestBodySchema,
      responses: {
        200: leaveRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject the step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    cancelRequest: {
      method: 'POST',
      path: '/requests/:leaveRequestId/cancel',
      body: cancelLeaveRequestBodySchema,
      responses: {
        /** Cancelled at once: the owner withdrawing a whole request before it starts (as always), or
         * a manager or HR acting on the person's behalf (`leave.cancel:on_behalf`). */
        200: leaveRequestSchema,
        /** Filed for approval instead — a partial cancel, or leave that has started or finished.
         * The request is untouched until `approveCancellationRequest`. */
        202: leaveCancellationRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Already cancelled, or a cancellation or amendment is already open on it. */
        409: errorBodySchema,
        412: errorBodySchema,
        /** A partial range outside the request or that would split it, or one for a pending request. */
        422: errorBodySchema,
      },
      summary:
        'Cancel leave: withdraw a request the caller filed, or cancel all or part of it — directly, ' +
        'or through approval when it has started or only part is given back',
      metadata: routeMeta(null),
    },

    getCalendar: {
      method: 'GET',
      path: '/calendar',
      query: getLeaveCalendarQuerySchema,
      responses: {
        200: paginatedSchema(leaveCalendarEntrySchema),
        401: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Team/org leave calendar within the caller's visible scope — peers for a plain employee, " +
        'direct/full reporting tree for a manager, the whole org (optionally narrowed) for HR',
      metadata: routeMeta(null),
    },

    listHolidays: {
      method: 'GET',
      path: '/holidays',
      query: listHolidaysQuerySchema,
      responses: {
        200: paginatedSchema(holidaySchema),
        401: errorBodySchema,
      },
      summary: "List a location's holiday calendar",
      metadata: routeMeta('leave.policy:read'),
    },

    createHoliday: {
      method: 'POST',
      path: '/holidays',
      body: createHolidayBodySchema,
      responses: {
        // One row per locationId in the body — every row shares the same name/date/isOptional/
        // isPublished, created atomically.
        201: z.array(holidaySchema),
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Stage one holiday event across one or more locations (holiday_calendar:manage)',
      metadata: routeMeta('holiday_calendar:manage'),
    },

    updateHoliday: {
      method: 'PATCH',
      path: '/holidays/:holidayId',
      body: updateHolidayBodySchema,
      responses: {
        200: holidaySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        // Editing `date` (or the location it belongs to, indirectly) can collide with the model's
        // own @@unique([orgId, locationId, date]) constraint, same as createHoliday.
        409: errorBodySchema,
      },
      summary: 'Edit one holiday row — e.g. publish it (holiday_calendar:manage)',
      metadata: routeMeta('holiday_calendar:manage'),
    },

    deleteHoliday: {
      method: 'DELETE',
      path: '/holidays/:holidayId',
      responses: {
        200: z.object({ id: uuidSchema }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        // A live LeaveRequest.holidayId reference refuses the delete rather than orphaning it.
        409: errorBodySchema,
      },
      summary: 'Remove one holiday row (holiday_calendar:manage)',
      metadata: routeMeta('holiday_calendar:manage'),
    },

    getSummary: {
      method: 'GET',
      path: '/summary',
      query: leaveSummaryQuerySchema,
      responses: {
        200: leaveSummarySchema,
        401: errorBodySchema,
      },
      summary: "Dashboard roll-up of the caller's available, taken, and pending-approval days",
      metadata: routeMeta(null),
    },

    listCompOffCredits: {
      method: 'GET',
      path: '/comp-off/credits',
      query: listCompOffCreditsQuerySchema,
      responses: {
        200: paginatedSchema(compOffCreditSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List a person's comp-off credits, e.g. from working a holiday",
      metadata: routeMeta('leave.balance:read'),
    },
  },
  { pathPrefix: '/leave' },
);
